import { EditAppointment } from "@/services/fhir";
import { PutPaymentNotice } from "@/services/fhir/paymentnotice/PutPaymentNotice";

/** Mesma extension usada no Painel de Controle (FutureViewer, agenda, etc.). */
export const CHARGE_NOTIFICATION_SENT_URL =
  "https://fluxmed.com.br/fhir/StructureDefinition/notification-sent";

const NOTIFICATION_SENT_EXTENSION = {
  url: CHARGE_NOTIFICATION_SENT_URL,
  valueBoolean: true,
} as const;

export function isChargeNotificationSentOnAppointment(
  appointment?: fhir5.Appointment | null
): boolean {
  return !!appointment?.extension?.find(
    (ext) => ext.url === CHARGE_NOTIFICATION_SENT_URL && ext.valueBoolean
  );
}

export function isChargeNotificationSentOnPayment(
  payment?: fhir5.PaymentNotice | null
): boolean {
  return !!payment?.extension?.find(
    (ext) => ext.url === CHARGE_NOTIFICATION_SENT_URL && ext.valueBoolean
  );
}

export function normalizeSlotId(slotRef?: string): string | null {
  if (!slotRef) return null;
  const trimmed = slotRef.trim();
  if (!trimmed) return null;
  const withoutPrefix = trimmed.replace(/^Slot\//i, "");
  return withoutPrefix.split("/").pop() || null;
}

export function mergeAppointmentsById(
  ...lists: Array<fhir5.Appointment[] | undefined>
): fhir5.Appointment[] {
  const byId = new Map<string, fhir5.Appointment>();
  lists.forEach((list) => {
    (list ?? []).forEach((appointment) => {
      if (appointment.id) byId.set(appointment.id, appointment);
    });
  });
  return Array.from(byId.values());
}

export function resolveAppointmentForSlot(
  slotRef: string | undefined,
  appointments: fhir5.Appointment[] = []
): fhir5.Appointment | null {
  if (!slotRef || appointments.length === 0) return null;

  const slotId = normalizeSlotId(slotRef);
  const normalized = slotRef.startsWith("Slot/") ? slotRef : `Slot/${slotRef}`;

  return (
    appointments.find((appointment) =>
      appointment.slot?.some((slot) => {
        const reference = String(slot.reference ?? "").trim();
        if (!reference) return false;
        if (reference === normalized) return true;
        if (slotId && normalizeSlotId(reference) === slotId) return true;
        return slotId ? reference.endsWith(`/${slotId}`) : false;
      })
    ) ?? null
  );
}

export type ChargeNotificationPaymentContext = {
  payment: fhir5.PaymentNotice;
  slot?: fhir5.Slot | null;
  appointment?: fhir5.Appointment | null;
};

export function resolveAppointmentForPaymentContext(
  paymentData: ChargeNotificationPaymentContext,
  appointments: fhir5.Appointment[] = []
): fhir5.Appointment | null {
  const slotRef =
    paymentData.payment.request?.reference ??
    (paymentData.slot?.id ? `Slot/${paymentData.slot.id}` : undefined);

  const bySlot = resolveAppointmentForSlot(slotRef, appointments);
  if (bySlot) return bySlot;

  return paymentData.appointment?.id ? paymentData.appointment : null;
}

/**
 * Cobrança enviada: prioriza Appointment (Painel de Controle) e aceita PaymentNotice
 * (relatórios / painel analítico quando o vínculo com consulta falhar no bundle).
 */
export function isPaymentChargeNotificationSent(
  paymentData: ChargeNotificationPaymentContext,
  options?: { appointments?: fhir5.Appointment[] }
): boolean {
  const appointment = resolveAppointmentForPaymentContext(
    paymentData,
    options?.appointments ?? []
  );
  if (isChargeNotificationSentOnAppointment(appointment)) return true;
  return isChargeNotificationSentOnPayment(paymentData.payment);
}

export async function markAppointmentChargeNotificationSent(
  appointment: fhir5.Appointment
): Promise<void> {
  if (!appointment.id || isChargeNotificationSentOnAppointment(appointment)) {
    return;
  }

  await EditAppointment({
    ...appointment,
    extension: [
      ...(appointment.extension || []),
      { ...NOTIFICATION_SENT_EXTENSION },
    ],
  });
}

export async function markPaymentChargeNotificationSent(
  paymentData: ChargeNotificationPaymentContext,
  options?: { appointments?: fhir5.Appointment[] }
): Promise<void> {
  const appointment = resolveAppointmentForPaymentContext(
    paymentData,
    options?.appointments ?? []
  );

  if (appointment?.id) {
    await markAppointmentChargeNotificationSent(appointment);
  }

  if (
    paymentData.payment.id &&
    !isChargeNotificationSentOnPayment(paymentData.payment)
  ) {
    await PutPaymentNotice({
      paymentId: paymentData.payment.id,
      data: {
        ...paymentData.payment,
        extension: [
          ...(paymentData.payment.extension || []),
          { ...NOTIFICATION_SENT_EXTENSION },
        ],
      },
    });
  }
}
